import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/communications/ConfirmDialog';
import { ConfigPanel, ConfigProblem } from './ConfigurationParts';
import { ConfigurationHistory } from './ConfigurationHistory';
import { configurationDate } from './configuration-date';
import { PurposeFields } from './PurposeFields';
import { usePurposeEditor } from './use-purpose-editor';
import type { Purpose } from './api';
type Editor = ReturnType<typeof usePurposeEditor>;
export function PurposePanel({
  id,
  onClose,
  onSaved,
}: {
  readonly id: string;
  readonly onClose: () => void;
  readonly onSaved: () => void;
}) {
  const { t } = useTranslation('expenses');
  const [confirm, setConfirm] = useState(false);
  const editor = usePurposeEditor(id, () => {
    toast.success(t('configuration.saved'));
    onSaved();
    if (id === 'new') onClose();
  });
  const { purpose, ready, error, busy } = editor;
  let title = t('configuration.details');
  if (id === 'new') title = t('configuration.new');
  else if (purpose) title = `${purpose.code} · ${purpose.label}`;
  const archive = async () => {
    await editor.save(false);
    setConfirm(false);
  };
  return (
    <ConfigPanel
      title={title}
      description={t(id === 'new' ? 'configuration.createSummary' : 'configuration.panelSummary')}
      onClose={onClose}
      footer={<PurposeFooter editor={editor} onArchive={() => setConfirm(true)} />}
    >
      {!ready && !error ? <p role="status">{t('configuration.loading')}</p> : null}
      {error && !(error === 'CONFIGURATION_CHANGED' && purpose) ? (
        <ConfigProblem code={error} retry={editor.reload} />
      ) : null}
      {error === 'CONFIGURATION_CHANGED' && purpose ? <PurposeConflict editor={editor} /> : null}
      {ready ? (
        <>
          <PurposeFields
            value={editor.value}
            onChange={editor.change}
            creating={id === 'new'}
            disabled={busy}
          />
          {purpose ? <PurposeReferences purpose={purpose} /> : null}
        </>
      ) : null}
      {confirm ? (
        <ConfirmDialog
          title={t('configuration.archiveTitle', { code: purpose?.code })}
          ask={t('configuration.archiveAsk')}
          confirmLabel={t('configuration.archive')}
          destructive
          busy={busy}
          onConfirm={() => void archive()}
          onClose={() => setConfirm(false)}
        />
      ) : null}
    </ConfigPanel>
  );
}
function PurposeFooter({
  editor: e,
  onArchive,
}: {
  readonly editor: Editor;
  readonly onArchive: () => void;
}) {
  const { t } = useTranslation('expenses');
  const p = e.purpose;
  const stale = isPurposeStale(e);
  const canArchive = p && (p.can.archive || p.can.restore);
  let reason = '';
  if (stale) reason = t('configuration.staleSaveReason');
  else if (!e.valid) reason = t('configuration.saveReason');
  else if (!e.changed) reason = t('configuration.unchanged');
  else if (e.changed && p && !stale) reason = t('configuration.saveBeforeArchive');
  return (
    <>
      <div>
        {canArchive ? (
          <Button
            variant="outline"
            className="min-h-11"
            disabled={e.busy || stale || e.changed}
            onClick={() => (p.active ? onArchive() : void e.save(true))}
          >
            {t(`configuration.${p.active ? 'archive' : 'restore'}`)}
          </Button>
        ) : null}
      </div>
      <div className="space-y-1 text-right">
        <Button className="min-h-11" disabled={!canSavePurpose(e)} onClick={() => void e.save()}>
          {t(e.busy ? 'configuration.saving' : 'configuration.save')}
        </Button>
        {reason ? <p className="text-muted-foreground max-w-64 text-xs">{reason}</p> : null}
      </div>
    </>
  );
}
function PurposeConflict({ editor: e }: { readonly editor: Editor }) {
  const { t } = useTranslation('expenses');
  if (!e.purpose) return null;
  return (
    <div role="alert" className="space-y-2 text-sm">
      <p>
        {t('configuration.latest', {
          label: e.purpose.label,
          when: configurationDate(e.purpose.updatedAt),
        })}
      </p>
      <p>
        {t('configuration.latestTypes', {
          types: e.purpose.noteTypes.map((type) => t(`reports.noteType.${type}`)).join(', '),
        })}
      </p>
      <Button variant="outline" className="min-h-11" onClick={e.useLatest}>
        {t('configuration.keepEdits')}
      </Button>
    </div>
  );
}
function PurposeReferences({ purpose: p }: { readonly purpose: Purpose }) {
  const { t } = useTranslation('expenses');
  return (
    <>
      <div className="space-y-2 text-sm">
        <p>
          {t(`configuration.${p.active ? 'active' : 'archived'}`)} ·{' '}
          {t(`configuration.${p.restricted ? 'restricted' : 'open'}`)}
        </p>
        {p.restricted && p.reservedUserIds.length === 0 ? <p>{t('configuration.nobody')}</p> : null}
        {p.restricted ? (
          <Link
            className="text-foreground font-semibold hover:underline inline-flex min-h-11 items-center"
            to={`/expenses/settings/reserved-purposes?purpose=${encodeURIComponent(p.code)}`}
          >
            {t('configuration.reservedLink')}
            <span aria-hidden="true" className="ml-1">
              →
            </span>
          </Link>
        ) : null}
        <Link
          className="text-foreground font-semibold hover:underline inline-flex min-h-11 items-center"
          to={`/expenses/reports?period=all&purpose=${p.id}&purposeLabel=${encodeURIComponent(`${p.code} · ${p.label}`)}`}
        >
          {t('configuration.relatedNotes')}
          <span aria-hidden="true" className="ml-1">
            →
          </span>
        </Link>
      </div>
      <p className="bg-muted rounded-lg p-3 text-sm">{t('configuration.impact')}</p>
      <ConfigurationHistory id={p.id} kind="purpose" revision={p.revision} />
    </>
  );
}

function isPurposeStale(e: Editor) {
  return e.purpose !== null && e.revision !== e.purpose.revision;
}
function canSavePurpose(e: Editor) {
  return (
    e.ready &&
    e.valid &&
    e.changed &&
    !e.busy &&
    !isPurposeStale(e) &&
    e.purpose?.can.edit !== false
  );
}
